import { CheckIcon } from '@chakra-ui/icons'
import {
  Input,
  InputGroup,
  InputLeftElement,
  InputProps,
  InputRightAddon,
  InputRightElement,
  Spinner,
} from '@chakra-ui/react'
import { MutableRefObject } from 'react'
import { NumericFormat, NumberFormatValues } from 'react-number-format'

import { stripToNumber } from '@common/util/format'

// TODO(scott): make this more type safe in our form library
export type FormStatus = {
  submitting?: string[]
  success?: string[]
  error?: string[]
}

interface CustomInputProps extends Omit<InputProps, 'defaultValue'> {
  value: string
  setValue: (newValue: string) => void
  formatAs?: 'number' | 'phone' | 'verification'
  precision?: number
  prefix?: string
  suffix?: string
  allowNegative?: boolean
  status?: FormStatus
  leftElement?: any
  rightElement?: any
  rightAddon?: any
  onReturn?: (target: HTMLInputElement) => void | undefined
  onShiftReturn?: (target: HTMLInputElement) => void | undefined
  onCommandReturn?: (target: HTMLInputElement) => void | undefined
  inputRef?: MutableRefObject<HTMLInputElement>
  inputGroupWidth?: string
}

export const CustomInput = (props: CustomInputProps) => {
  const {
    value,
    setValue,
    formatAs,
    precision,
    prefix,
    suffix,
    allowNegative,
    onBlur,
    onFocus,
    status,
    leftElement,
    rightElement,
    rightAddon,
    onReturn,
    onShiftReturn,
    onCommandReturn,
    inputRef,
    inputGroupWidth,
    size,
    ...rest
  } = props

  const format = (s: string) => {
    if (formatAs === 'phone') {
      return formatPhone(s)
    } else if (formatAs === 'verification') {
      return formatVerification(s)
    }
    return s
  }

  const formatPhone = (s: string) => {
    let input = stripToNumber(s.trim().replace('+1', ''))
    if (input.length === 0) {
      return input
    }
    const size = input.length
    if (size > 0) {
      input = '(' + input
    }
    if (size > 3) {
      input = input.slice(0, 4) + ') ' + input.slice(4, 11)
    }
    if (size > 6) {
      input = input.slice(0, 9) + '-' + input.slice(9, 13)
    }
    return input
  }

  const formatVerification = (s: string) => {
    let input = stripToNumber(s.trim())
    if (input.length === 0) {
      return input
    }
    const size = input.length
    if (size > 3) {
      input = input.slice(0, 3) + '-' + input.slice(3, 6)
    }
    return input
  }

  const parse = (s: string) => {
    if (formatAs === 'phone') {
      return parsePhone(s)
    } else if (formatAs === 'verification') {
      return parseVerification(s)
    }
    return s
  }

  const parsePhone = (s: string) => {
    return stripToNumber(s).substring(0, 10)
  }

  const parseVerification = (s: string) => {
    return stripToNumber(s).substring(0, 6)
  }

  const submitting = !!rest.name && status?.submitting?.includes(rest.name)
  const success = !!rest.name && status?.success?.includes(rest.name)
  const error = !!rest.name && status?.error?.includes(rest.name)

  const commonProps = {
    ...rest,
    onFocus,
    onBlur,
    borderColor: rest.borderColor ?? (success ? 'success' : undefined),
    color: rest.color ?? (success ? 'success' : undefined),
    isInvalid: error,
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
      rest.onKeyDown?.(e)
      if (e.defaultPrevented) return
      const target = e.target as HTMLInputElement
      if (e.key === 'Enter') {
        if (e.metaKey === true && onCommandReturn !== undefined) {
          e.preventDefault()
          onCommandReturn(target)
        } else if (e.shiftKey === true && onShiftReturn !== undefined) {
          e.preventDefault()
          onShiftReturn(target)
        } else if (e.shiftKey === false) {
          e.preventDefault()
          if (onReturn) {
            onReturn(target)
          } else {
            e.currentTarget.blur()
          }
        }
      }
    },
  }

  const getInputPadding = () => {
    let paddingLeft = '1rem'
    let paddingRight = '1rem'

    if (leftElement) {
      paddingLeft = '2.5rem'
    }
    if (!rightAddon && (success || submitting || rightElement)) {
      paddingRight = '2.5rem'
    }
    if (rightAddon) {
      paddingRight = '4.5rem'
    }

    return { paddingLeft, paddingRight }
  }

  const renderInput = () => {
    if (formatAs === 'number') {
      const padding = getInputPadding()
      return (
        <NumericFormat
          {...commonProps}
          value={value}
          thousandSeparator=","
          decimalScale={precision}
          prefix={prefix}
          suffix={suffix}
          allowNegative={allowNegative}
          onValueChange={(values: NumberFormatValues, sourceInfo) => {
            // Controlled inputs already own `value`; ignore reformats triggered by prop updates.
            if (sourceInfo.source === 'prop') {
              return
            }
            setValue(values.value)
          }}
          customInput={Input}
          type="text"
          paddingLeft={padding.paddingLeft}
          paddingRight={padding.paddingRight}
        />
      )
    } else {
      return (
        <Input
          {...commonProps}
          ref={inputRef}
          onChange={e => {
            const newValue = parse(e.target.value)
            setValue(newValue)
          }}
          value={format(value)}
        />
      )
    }
  }

  return (
    <InputGroup width={inputGroupWidth} size={size}>
      {leftElement && <InputLeftElement>{leftElement}</InputLeftElement>}
      {renderInput()}
      {!rightAddon && (success || submitting || rightElement) && (
        <InputRightElement>
          {success && <CheckIcon color="success" />}
          {!success && submitting && <Spinner size="sm" />}
          {!success && !submitting && rightElement}
        </InputRightElement>
      )}
      {rightAddon && (
        <InputRightAddon>
          {success && <CheckIcon color="success" />}
          {!success && submitting && <Spinner size="sm" />}
          {!success && !submitting && rightAddon}
        </InputRightAddon>
      )}
    </InputGroup>
  )
}
